import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { makeStyles, useTheme } from "@mui/styles";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Modal,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import translations from "app/libs/i18n/translations.json";
import {
  companyIndustryMap,
  availabilities,
  candidateStatus,
  experience,
  degrees,
} from "bundles/common/utils/constants";
import sh from "bundles/common/utils/sh";
import formatIntegerDotThousands from "bundles/common/utils/formatIntegerDotThousands";

const useStyles = makeStyles((theme) => ({
  modalContent: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: theme.palette.common.white,
    borderRadius: "16px",
    [theme.breakpoints.down("sm")]: {
      borderBottomLeftRadius: "0 !important",
      borderBottomRightRadius: "0 !important",
    },
    width: "min(860px, calc(100vw - 32px))",
    maxHeight: "min(820px, calc(100vh - 32px))",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      top: "10vh",
      transform: "translate(-50%, 0)",
      maxHeight: "calc(100vh - 10vh)",
    },
    outline: "none",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 20px",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  badge: {
    borderRadius: "999px",
    padding: "2px 8px",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    background: theme.palette.secondary.hover,
    color: theme.palette.text.main,
  },
  closeBtn: {
    cursor: "pointer",
    color: theme.palette.text.secondary,
    "&:hover": { color: theme.palette.text.primary},
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
  },
  body: {
    padding: "0 20px 16px 20px",
    overflowY: "auto",
  },
  section: {
    marginBottom: "20px",
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "15px",
  },
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
  },
  chip: {
    borderRadius: "20px",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    background: theme.palette.background.default,
    border: "none",
    color: theme.palette.text.main,
    height: "28px",
    "&&:hover": {
      background: theme.palette.secondary.hover,
      color: theme.palette.text.main,
    },
  },
  chipSelected: {
    background: theme.palette.secondary.hover,
    color: theme.palette.text.main,
  },
  footer: {
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
  },
  applyBtn: {
    borderRadius: "999px",
    padding: "10px 28px",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    textTransform: "none",
    background: theme.palette.primary.dark,
    color: theme.palette.common.white,
    "&:hover": {
      background: theme.palette.secondary.hover,
      color: theme.palette.text.main,
    },
    "&:active": {
      background: theme.palette.text.main,
    }
  },
  resetBtn: {
    textTransform: "none",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    borderRadius: "999px",
    "&:hover": {
      borderRadius: "999px",
    }
  },
  accordion: {
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:before": { display: "none" },
    "&.Mui-expanded": {
      margin: "0 !important",
    },
    "&:first-of-type": {
      borderTopLeftRadius: "20px",
      borderTopRightRadius: "20px",
    },
    "&:last-of-type": {
      borderBottomLeftRadius: "20px",
      borderBottomRightRadius: "20px",
    },
  },
  accordionSummary: {
    paddingLeft: "14px",
    paddingRight: "14px",
    "&.Mui-expanded": {
      minHeight: "52px !important",
    },
    "& .MuiAccordionSummary-content": { margin: 0 },
    "$accordion:first-of-type &": {
      borderTopLeftRadius: "20px",
      borderTopRightRadius: "20px",
    },
    "$accordion:last-of-type &": {
      borderBottomLeftRadius: "20px",
      borderBottomRightRadius: "20px",
    },
    "$accordion:last-of-type.Mui-expanded &": {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
  accordionDetails: {
    padding: "12px 14px 16px 14px",
    "$accordion:last-of-type &": {
      borderBottomLeftRadius: "20px",
      borderBottomRightRadius: "20px",
    },
  },
  accordionTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: "10px",
  },
  autocompleteWrapper: {
    backgroundColor: theme.palette.grey[100] || "#F4F4F6",
    borderRadius: "20px",
    padding: "4px 16px",
  },
  autocomplete: {
    "& .MuiInputBase-root": {
      border: "none",
      padding: "0 !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiAutocomplete-input": {
      padding: "8px 0 !important",
      color: theme.palette.text.primary || "#111",
      "&::placeholder": {
        opacity: 0.7,
      },
    },
    "& .MuiChip-root": {
      backgroundColor: theme.palette.text.main,
      color: theme.palette.common.white,
    },
    "& .MuiChip-deleteIcon": {
      color: theme.palette.common.white,
      "&:hover": { color: theme.palette.common.white, opacity: 0.8 },
    },
  },
  dropdownPaper: {
    borderRadius: "20px",
    marginTop: "8px",
    "& .MuiAutocomplete-listbox": {
      paddingTop: 0,
      cursor: "pointer",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
  },
  autocompleteOption: {
    padding: "12px 16px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover || "#F4F4F6",
    },
    "&[aria-selected='true']": {
      backgroundColor: theme.palette.common.blue_grey || "#E8F4FD",
    },
  },
  selectedHint: {
    color: theme.palette.text.secondary || "#666",
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
}));

const toggleInArray = (arr, value) => {
  const set = new Set(arr || []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return Array.from(set);
};

const BOOLEAN_FIELD_VALUES = [
  "first_name",
  "last_name",
  "country",
  "city",
  "region",
  "presentation",
  "job_title",
  "desired_job_title",
];

const BOOLEAN_FIELD_DEFAULT_LABELS = {
  first_name: "First name",
  last_name: "Last name",
  country: "Country",
  city: "City",
  region: "Region",
  presentation: "Presentation",
  job_title: "Job title",
  desired_job_title: "Desired job title",
};

const BOOLEAN_OPERATOR_VALUES = [
  {
    value: "include_all",
    labelId: "recruiter.cv_theque.boolean_op.include_all",
    defaultLabel: "Include all words",
  },
  {
    value: "include_any",
    labelId: "recruiter.cv_theque.boolean_op.include_any",
    defaultLabel: "Include any word",
  },
  {
    value: "exclude",
    labelId: "recruiter.cv_theque.boolean_op.exclude",
    defaultLabel: "Exclude",
  },
];

const BOOLEAN_OPERATORS_ALLOWED = new Set(BOOLEAN_OPERATOR_VALUES.map((o) => o.value));

const emptyBooleanClause = () => ({
  field: "job_title",
  operator: "include_all",
  term: "",
});

const migrateBooleanClause = (clause) => {
  if (!clause || typeof clause !== "object") return emptyBooleanClause();
  let op = clause.operator === "include" ? "include_all" : clause.operator;
  if (!BOOLEAN_OPERATORS_ALLOWED.has(op)) op = "include_all";
  return {
    field: BOOLEAN_FIELD_VALUES.includes(clause.field) ? clause.field : "job_title",
    operator: op,
    term: clause.term != null ? String(clause.term) : "",
  };
};

const sanitizeBooleanClauses = (clauses) => {
  if (!Array.isArray(clauses)) return [];
  const allowedFields = new Set(BOOLEAN_FIELD_VALUES);
  return clauses
    .map((clause) => {
      let op = clause?.operator === "include" ? "include_all" : clause?.operator;
      if (!BOOLEAN_OPERATORS_ALLOWED.has(op)) op = "include_all";
      return {
        field: allowedFields.has(clause?.field) ? clause.field : "job_title",
        operator: op,
        term: (clause?.term || "").toString(),
      };
    })
    .filter((clause) => clause.term.trim().length > 0);
};

const LANGUAGES = [
  { code: "fr", labelId: "language.french", defaultLabel: "French" },
  { code: "nl", labelId: "language.dutch", defaultLabel: "Dutch" },
  { code: "en", labelId: "language.english", defaultLabel: "English" },
];

const LANGUAGE_SKILLS_BY_CODE = {
  fr: "french",
  nl: "dutch",
  en: "english",
};

const LAST_ACTIVE_OPTIONS = [
  { value: 7, labelId: "recruiter.cv_theque.last_active_7", defaultLabel: "Last 7 days" },
  { value: 30, labelId: "recruiter.cv_theque.last_active_30", defaultLabel: "Last 30 days" },
  { value: 90, labelId: "recruiter.cv_theque.last_active_90", defaultLabel: "Last 90 days" },
];

const rangesEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};


const buildFiltersFromDraftValue = (draftValue) => {
  let filters = {};
  const languageSkills = (draftValue.languages || [])
    .map((languageCode) => LANGUAGE_SKILLS_BY_CODE[languageCode])
    .filter(Boolean);

  if (draftValue.onlyFavorites) {
    filters.only_favorites = true;
  }
  const locations = (draftValue.locations || []).flatMap((loc) => {
    if (!loc) return [];
    if (loc.type === "town") {
      return loc.lat != null && loc.lng != null ? [{ type: "town", label: loc.label, lat: loc.lat, lng: loc.lng }] : [];
    }
    if (loc.type === "country") return [{ type: "country", code: loc.countryCode }];
    return [{ type: "region", code: loc.code }];
  });
  if (locations.length > 0) {
    filters.locations = locations;
    if (draftValue.radiusKm > 0) {
      filters.radius_km = draftValue.radiusKm;
    }
  }
  if (draftValue.availabilities?.length > 0) {
    filters.availability = draftValue.availabilities;
  }
  if (draftValue.sectors?.length > 0) {
    filters.desired_sector = draftValue.sectors;
  }
  if (draftValue.experienceRanges?.length > 0) {
    let experienceArray = [];
    for (let i = 0; i < draftValue.experienceRanges.length; i++) {
      experienceArray = experienceArray.concat(draftValue.experienceRanges[i] || []);
    }
    filters.experience_year = experienceArray.map(String);
  }
  if (draftValue.hardSkills?.length > 0 || draftValue.softSkills?.length > 0 || languageSkills.length > 0) {
    filters.skills = (draftValue.hardSkills || [])
      .concat(draftValue.softSkills || [])
      .concat(languageSkills);
  }
  if (draftValue.highestDegrees?.length > 0) {
    filters.highest_degree = draftValue.highestDegrees;
  }
  if (draftValue.candidateStatuses?.length > 0) {
    filters.candidate_status = draftValue.candidateStatuses;
  }
  if (draftValue.activeWithin) {
    filters.active_within = draftValue.activeWithin;
  }
  return filters;
};

const FiltersCandidatesModal = ({
  open,
  onClose,
  onApply,
  candidatesCount = 0,
  appliedFilters,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const authenticity_token = useSelector((state) => state.authenticity_token);
  // TODO: Add facets and counts when we have higher number of candidates that completes their profile
  // const [facets, setFacets] = useState({});
  // const [facetsLoaded, setFacetsLoaded] = useState(false);
  // const [, setFacetsLoading] = useState(false);
  const [filteredCount, setFilteredCount] = useState(candidatesCount);
  //
  // const facetsRequestIdRef = useRef(0);
  // const lastFacetsFiltersJsonRef = useRef(null);

  const hardSkillsList = useMemo(() => {
    return Object.entries(translations[i18nLocale] || {})
      .filter((elem) => elem[0].includes("skills_list.hard"))
      .map((elem) => elem[0].split(".").slice(-1))
      .flat()
      .sort();
  }, [i18nLocale]);

  const softSkillsList = useMemo(() => {
    return Object.entries(translations[i18nLocale] || {})
      .filter((elem) => elem[0].includes("skills_list.soft"))
      .map((elem) => elem[0].split(".").slice(-1))
      .flat()
      .sort();
  }, [i18nLocale]);

  const [draft, setDraft] = useState(() => ({
    onlyFavorites: false,
    booleanClauses: [emptyBooleanClause()],
    booleanExpression: "",
    languages: [],
    sectors: [],
    availabilities: [],
    experienceRanges: [],
    hardSkills: [],
    softSkills: [],
    locations: [],
    radiusKm: 0,
    highestDegrees: [],
    candidateStatuses: [],
    activeWithin: null,
  }));

  // returns the number of filters applied, used to show the badge count
  // const fetchFacets = useCallback((filtersObj = {}) => {
  //   const filtersJson = JSON.stringify(filtersObj || {});
  //   if (lastFacetsFiltersJsonRef.current === filtersJson) return;
  //
  //   lastFacetsFiltersJsonRef.current = filtersJson;
  //   const requestId = (facetsRequestIdRef.current += 1);
  //
  //   setFacetsLoading(true);
  //
  //   sh.get("search/candidates/facets", {
  //     params: {
  //       authenticity_token,
  //       filters: filtersJson,
  //     },
  //   })
  //     .then((res) => {
  //       if (requestId !== facetsRequestIdRef.current) return;
  //       setFacets(res.data?.facets || {});
  //       setFacetsLoaded(true);
  //     })
  //     .catch((err) => {
  //       console.error("Failed to fetch facets:", err);
  //     })
  //     .finally(() => {
  //       if (requestId !== facetsRequestIdRef.current) return;
  //       setFacetsLoading(false);
  //     });
  // }, [authenticity_token]);

  useEffect(() => {
    if (!open) return;
    const next = appliedFilters || {};
    const nextDraft = {
      onlyFavorites: Boolean(next.onlyFavorites),
      booleanClauses: Array.isArray(next.booleanClauses) && next.booleanClauses.length > 0
        ? next.booleanClauses.map(migrateBooleanClause)
        : [emptyBooleanClause()],
      booleanExpression: typeof next.booleanExpression === "string" ? next.booleanExpression : "",
      languages: next.languages || [],
      sectors: next.sectors || [],
      availabilities: next.availabilities || [],
      experienceRanges: next.experienceRanges || [],
      hardSkills: next.hardSkills || [],
      softSkills: next.softSkills || [],
      locations: next.locations || [],
      radiusKm: next.radiusKm || 0,
      highestDegrees: next.highestDegrees || [],
      candidateStatuses: next.candidateStatuses || [],
      activeWithin: next.activeWithin || null,
    };

    setDraft(nextDraft);
    // fetchFacets(buildFiltersFromDraftValue(nextDraft));
  }, [open, appliedFilters]);

  const buildFiltersFromDraft = useCallback(() => buildFiltersFromDraftValue(draft), [draft]);

  useEffect(() => {
    if (!open) return;

    const filters = buildFiltersFromDraft();
    const cleanedClauses = sanitizeBooleanClauses(draft.booleanClauses);
    const booleanExpression = (draft.booleanExpression || "").trim();
    const hasFilters =
      Object.keys(filters).length > 0 || cleanedClauses.length > 0 || booleanExpression.length > 0;

    if (!hasFilters) {
      setFilteredCount(candidatesCount);
      return;
    }

    const debounceTimer = setTimeout(() => {
      sh.get("search/candidates", {
        cancelKey: "cvtheque-filters-preview-count",
        params: {
          authenticity_token,
          query: "",
          page: 1,
          filters: JSON.stringify(filters),
          boolean_clauses: JSON.stringify(cleanedClauses),
          boolean_expression: booleanExpression,
        },
      })
        .then((res) => {
          setFilteredCount(res.data?.pagy?.count || 0);
        })
        .catch((err) => {
          console.error("Failed to fetch filtered count:", err);
        });
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [
    open,
    draft,
    authenticity_token,
    candidatesCount,
    buildFiltersFromDraft,
  ]);
  //
  // const counts = useMemo(() => {
  //   const languageCounts = facets.preferred_locale || {};
  //   const sectorCounts = facets.desired_sector || {};
  //   const availabilityCounts = facets.availability || {};
  //   const degreeCounts = facets.highest_degree || {};
  //   const regionCounts = facets.region || {};
  //
  //   const countryCounts = {
  //     belgium: be_regions.reduce((sum, r) => sum + (regionCounts[r] || 0), 0),
  //     luxembourg: lu_regions.reduce((sum, r) => sum + (regionCounts[r] || 0), 0),
  //     france: fr_regions.reduce((sum, r) => sum + (regionCounts[r] || 0), 0),
  //   };
  //
  //   const experienceRangeCounts = {};
  //   const expYearCounts = facets.experience_year || {};
  //   experience.forEach((xp) => {
  //     experienceRangeCounts[xp.id] = xp.value.reduce(
  //       (sum, year) => sum + (expYearCounts[String(year)] || 0),
  //       0
  //     );
  //   });
  //
  //   return {
  //     languageCounts,
  //     sectorCounts,
  //     availabilityCounts,
  //     degreeCounts,
  //     regionCounts,
  //     countryCounts,
  //     experienceRangeCounts,
  //   };
  // }, [facets]);

  const onReset = () => {
    const nextDraft = {
      onlyFavorites: false,
      booleanClauses: [emptyBooleanClause()],
      booleanExpression: "",
      languages: [],
      sectors: [],
      availabilities: [],
      experienceRanges: [],
      hardSkills: [],
      softSkills: [],
      locations: draft.locations || [],
      radiusKm: draft.radiusKm || 0,
      highestDegrees: [],
      candidateStatuses: [],
      activeWithin: null,
    };

    setDraft(nextDraft);
    // fetchFacets(buildFiltersFromDraftValue(nextDraft));
  };

  const handleApply = () => {
    const cleanedClauses = sanitizeBooleanClauses(draft.booleanClauses);
    const booleanExpression = (draft.booleanExpression || "").trim();
    if (typeof onApply === "function") {
      onApply({ ...draft, booleanClauses: cleanedClauses, booleanExpression });
    }
    if (typeof onClose === "function") onClose();
  };

  const toggleExperienceRange = (xpValue) => {
    setDraft((prev) => {
      const existing = prev.experienceRanges || [];
      const has = existing.some((r) => rangesEqual(r, xpValue));
      const nextDraft = {
        ...prev,
        experienceRanges: has
          ? existing.filter((r) => !rangesEqual(r, xpValue))
          : [...existing, xpValue],
      };
      // fetchFacets(buildFiltersFromDraftValue(nextDraft));
      return nextDraft;
    });
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="advanced-filters-modal">
      <Paper className={classes.modalContent}>
        <Box className={classes.header}>
          <Typography variant="h4" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Bold, system-ui, sans-serif", fontWeight: 800 }}>
            <FormattedMessage id="filters" defaultMessage="Filters" />
          </Typography>
          <Box className={classes.headerRight}>
            <Typography variant="body2" sx={{ color: "#111", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontSize: "16px" }}>
              <FormattedMessage id="candidates" defaultMessage="Candidates" />
            </Typography>
            <Box className={classes.badge}>
              <Typography
                variant="body2"
                sx={{
                  color: "#111",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatIntegerDotThousands(filteredCount)}
              </Typography>
            </Box>
            <CloseIcon className={classes.closeBtn} onClick={onClose} />
          </Box>
        </Box>

        <Box className={classes.body}>
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.boolean_search" defaultMessage="Boolean search" />
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: "20px" }}>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}
              >
                <FormattedMessage
                  id="recruiter.cv_theque.boolean_expression_label"
                  defaultMessage="Custom boolean query"
                />
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text?.secondary, display: "block" }}
              >
                <FormattedMessage
                  id="recruiter.cv_theque.boolean_expression_hint"
                  defaultMessage='Write your own query with AND, OR, NOT, parentheses and "quoted phrases". At least one of the words inside each group of parentheses must appear in the candidate profile. Example: (dessinateur OR draughtsman OR "design engineer") AND (piping OR tuyauterie) AND SolidWorks'
                />
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="outlined"
                placeholder={intl.formatMessage({
                  id: "recruiter.cv_theque.boolean_expression_placeholder",
                  defaultMessage:
                    "(dessinateur OR draughtsman OR \"design engineer\") AND (piping OR tuyauterie) AND SolidWorks",
                })}
                value={draft.booleanExpression || ""}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, booleanExpression: e.target.value }))
                }
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text?.secondary, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}
              >
                <FormattedMessage
                  id="recruiter.cv_theque.boolean_field_by_field"
                  defaultMessage="Or search field by field"
                />
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text?.secondary, display: "block", mb: 1 }}
              >
                <FormattedMessage
                  id="recruiter.cv_theque.boolean_words_hint"
                  defaultMessage="Separate words with spaces. Include all: every word must appear in the field. Include any: at least one word. Exclude: hide profiles that contain any of these words."
                />
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {(draft.booleanClauses || [emptyBooleanClause()]).map((clause, idx) => (
                  <Box
                    key={`boolean-clause-${idx}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "200px minmax(200px, 260px) 1fr auto" },
                      gap: "12px",
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      select
                      label={intl.formatMessage({ id: "recruiter.cv_theque.field_name", defaultMessage: "Field name" })}
                      value={clause.field || "job_title"}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          booleanClauses: (prev.booleanClauses || []).map((row, rowIdx) => (
                            rowIdx === idx ? { ...row, field: e.target.value } : row
                          )),
                        }))
                      }
                    >
                      {BOOLEAN_FIELD_VALUES.map((fieldValue) => (
                        <MenuItem key={fieldValue} value={fieldValue}>
                          {intl.formatMessage({
                            id: `recruiter.cv_theque.boolean_field.${fieldValue}`,
                            defaultMessage: BOOLEAN_FIELD_DEFAULT_LABELS[fieldValue] || fieldValue,
                          })}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label={intl.formatMessage({ id: "recruiter.cv_theque.boolean_operator", defaultMessage: "Operator" })}
                      value={
                        BOOLEAN_OPERATORS_ALLOWED.has(clause.operator) ? clause.operator : "include_all"
                      }
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          booleanClauses: (prev.booleanClauses || []).map((row, rowIdx) => (
                            rowIdx === idx ? { ...row, operator: e.target.value } : row
                          )),
                        }))
                      }
                    >
                      {BOOLEAN_OPERATOR_VALUES.map((operator) => (
                        <MenuItem key={operator.value} value={operator.value}>
                          {intl.formatMessage({
                            id: operator.labelId,
                            defaultMessage: operator.defaultLabel,
                          })}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={intl.formatMessage({
                        id: "recruiter.cv_theque.boolean_free_text",
                        defaultMessage: "Free text",
                      })}
                      value={clause.term || ""}
                      onChange={(e) =>
                        setDraft((prev) => ({
                          ...prev,
                          booleanClauses: (prev.booleanClauses || []).map((row, rowIdx) => (
                            rowIdx === idx ? { ...row, term: e.target.value } : row
                          )),
                        }))
                      }
                    />
                    <Button
                      disabled={(draft.booleanClauses || []).length <= 1}
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          booleanClauses: (prev.booleanClauses || []).filter((_, rowIdx) => rowIdx !== idx),
                        }))
                      }
                    >
                      <FormattedMessage id="recruiter.cv_theque.boolean_remove_row" defaultMessage="Remove" />
                    </Button>
                  </Box>
                ))}
                <Box>
                  <Button
                    onClick={() =>
                      setDraft((prev) => ({
                        ...prev,
                        booleanClauses: [...(prev.booleanClauses || []), emptyBooleanClause()],
                      }))
                    }
                  >
                    <FormattedMessage id="recruiter.cv_theque.add_field_to_search" defaultMessage="+ Add a field to search" />
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.language" defaultMessage="Language" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              {LANGUAGES.map((lang) => {
                const isSelected = (draft.languages || []).includes(lang.code);
                const label = intl.formatMessage({
                  id: lang.labelId,
                  defaultMessage: lang.defaultLabel,
                });
                return (
                  <Chip
                    key={lang.code}
                    label={label}
                    className={`${classes.chip} ${isSelected ? classes.chipSelected : ""}`}
                    onClick={() => {
                      setDraft((prev) => ({
                        ...(() => {
                          const nextDraft = {
                            ...prev,
                            languages: toggleInArray(prev.languages, lang.code),
                          };
                          // fetchFacets(buildFiltersFromDraftValue(nextDraft));
                          return nextDraft;
                        })(),
                      }));
                    }}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Sectors */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.sectors" defaultMessage="Sectors" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              {companyIndustryMap.map((sector) => {
                const isSelected = (draft.sectors || []).includes(sector);
                const label = intl.formatMessage({
                  id: `company.company_industry.${sector}`,
                  defaultMessage: sector,
                });
                return (
                  <Chip
                    key={sector}
                    label={label}
                    className={`${classes.chip} ${isSelected ? classes.chipSelected : ""}`}
                    onClick={() => {
                      setDraft((prev) => ({
                        ...(() => {
                          const nextDraft = {
                            ...prev,
                            sectors: toggleInArray(prev.sectors, sector),
                          };
                          // fetchFacets(buildFiltersFromDraftValue(nextDraft));
                          return nextDraft;
                        })(),
                      }));
                    }}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Availability */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.availability" defaultMessage="Disponibility" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              {availabilities.map((a) => {
                const isSelected = (draft.availabilities || []).includes(a);
                const label = intl.formatMessage({ id: `candidate.${a}`, defaultMessage: a });
                return (
                  <Chip
                    key={a}
                    label={label}
                    className={`${classes.chip} ${isSelected ? classes.chipSelected : ""}`}
                    onClick={() => {
                      setDraft((prev) => ({
                        ...(() => {
                          const nextDraft = {
                            ...prev,
                            availabilities: toggleInArray(prev.availabilities, a),
                          };
                          // fetchFacets(buildFiltersFromDraftValue(nextDraft));
                          return nextDraft;
                        })(),
                      }));
                    }}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Last active */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.last_active" defaultMessage="Last active" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              {LAST_ACTIVE_OPTIONS.map((option) => {
                const isSelected = draft.activeWithin === option.value;
                const label = intl.formatMessage({ id: option.labelId, defaultMessage: option.defaultLabel });
                return (
                  <Chip
                    key={option.value}
                    label={label}
                    className={`${classes.chip} ${isSelected ? classes.chipSelected : ""}`}
                    onClick={() => {
                      setDraft((prev) => ({
                        ...prev,
                        activeWithin: prev.activeWithin === option.value ? null : option.value,
                      }));
                    }}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Years of experience */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.years_of_experience" defaultMessage="Years of experience" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              {experience.map((xp) => {
                const isSelected = (draft.experienceRanges || []).some((r) => rangesEqual(r, xp.value));
                return (
                  <Chip
                    key={xp.id}
                    label={`${xp.label} ${intl.formatMessage({ id: "candidate.years", defaultMessage: "years" })}`}
                    className={`${classes.chip} ${isSelected ? classes.chipSelected : ""}`}
                    onClick={() => {
                      toggleExperienceRange(xp.value);
                    }}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Skills */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.skills" defaultMessage="Skills" />
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", border: "0.5px solid #DADADA", borderRadius: "20px" }}>
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={classes.accordionSummary}
                >
                  <Box className={classes.accordionTopRow}>
                    <Chip
                      size="small"
                      label={intl.formatMessage({ id: "candidate.profile.skills.hard", defaultMessage: "Hard skills" })}
                      className={classes.chip}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Box className={classes.autocompleteWrapper}>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      className={classes.autocomplete}
                      options={hardSkillsList}
                      value={draft.hardSkills || []}
                      onChange={(_e, newValue) =>
                        setDraft((prev) => {
                          const nextDraft = { ...prev, hardSkills: newValue };
                          // fetchFacets(buildFiltersFromDraftValue(nextDraft));
                          return nextDraft;
                        })
                      }
                      getOptionLabel={(opt) =>
                        translations[i18nLocale]?.[`skills_list.hard.${opt}`] || opt
                      }
                      PaperComponent={({ children, ...props }) => (
                        <Paper {...props} className={classes.dropdownPaper}>
                          {children}
                        </Paper>
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option} className={classes.autocompleteOption}>
                          <Typography variant="body2">
                            {translations[i18nLocale]?.[`skills_list.hard.${option}`] || option}
                          </Typography>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={intl.formatMessage({ id: "autocomplete", defaultMessage: "Search…" })}
                        />
                      )}
                      componentsProps={{
                        popper: {
                          placement: "bottom-start",
                          modifiers: [
                            {
                              name: "offset",
                              options: {
                                offset: [-16, -1],
                              },
                            },
                          ],
                          sx: {
                            width: "770px !important"
                          }
                        },
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion className={classes.accordion}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={classes.accordionSummary}
                >
                  <Box className={classes.accordionTopRow}>
                    <Chip
                      size="small"
                      label={intl.formatMessage({ id: "candidate.profile.skills.soft", defaultMessage: "Soft skills" })}
                      className={classes.chip}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Box className={classes.autocompleteWrapper}>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      className={classes.autocomplete}
                      options={softSkillsList}
                      value={draft.softSkills || []}
                      onChange={(_e, newValue) =>
                        setDraft((prev) => {
                          const nextDraft = { ...prev, softSkills: newValue };
                          // fetchFacets(buildFiltersFromDraftValue(nextDraft));
                          return nextDraft;
                        })
                      }
                      getOptionLabel={(opt) =>
                        translations[i18nLocale]?.[`skills_list.soft.${opt}`] || opt
                      }
                      PaperComponent={({ children, ...props }) => (
                        <Paper {...props} className={classes.dropdownPaper}>
                          {children}
                        </Paper>
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option} className={classes.autocompleteOption}>
                          <Typography variant="body2">
                            {translations[i18nLocale]?.[`skills_list.soft.${option}`] || option}
                          </Typography>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder={intl.formatMessage({ id: "autocomplete", defaultMessage: "Search…" })}
                        />
                      )}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>

          {/* Diplomas */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.diplomas" defaultMessage="Diplomas" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              {degrees.map((d) => {
                const isSelected = (draft.highestDegrees || []).includes(d.code);
                const label = intl.formatMessage({ id: d.label, defaultMessage: d.code });
                return (
                  <Chip
                    key={d.code}
                    label={label}
                    className={`${classes.chip} ${isSelected ? classes.chipSelected : ""}`}
                    onClick={() => {
                      setDraft((prev) => ({
                        ...(() => {
                          const nextDraft = {
                            ...prev,
                            highestDegrees: toggleInArray(prev.highestDegrees, d.code),
                          };
                          // fetchFacets(buildFiltersFromDraftValue(nextDraft));
                          return nextDraft;
                        })(),
                      }));
                    }}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Candidate status */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="recruiter.cv_theque.candidate_status" defaultMessage="Candidate status" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              {candidateStatus.map((status) => {
                const isSelected = (draft.candidateStatuses || []).includes(status);
                const label = intl.formatMessage({
                  id: `candidate.status_map.${status}`,
                  defaultMessage: status,
                });
                return (
                  <Chip
                    key={status}
                    label={label}
                    className={`${classes.chip} ${isSelected ? classes.chipSelected : ""}`}
                    onClick={() => {
                      setDraft((prev) => ({
                        ...prev,
                        candidateStatuses: toggleInArray(prev.candidateStatuses, status),
                      }));
                    }}
                    variant={isSelected ? "filled" : "outlined"}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Saved candidates */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitleRow}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text?.main, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id="nav.recruiter.favorite_candidates" defaultMessage="Saved candidates" />
              </Typography>
            </Box>
            <Box className={classes.chipsWrap}>
              <Chip
                label={intl.formatMessage({
                  id: "recruiter.cv_theque.only_saved_candidates",
                  defaultMessage: "Only saved candidates",
                })}
                className={`${classes.chip} ${draft.onlyFavorites ? classes.chipSelected : ""}`}
                onClick={() => setDraft((prev) => ({ ...prev, onlyFavorites: !prev.onlyFavorites }))}
                variant={draft.onlyFavorites ? "filled" : "outlined"}
              />
            </Box>
          </Box>

        </Box>

        <Box className={classes.footer}>
          <Button className={classes.resetBtn} onClick={onReset}>
            <FormattedMessage id="job_search.clear_filters" defaultMessage="Clear filters" />
          </Button>
          <Button className={classes.applyBtn} onClick={handleApply}>
            <FormattedMessage
              id="recruiter.cv_theque.show_n_candidates"
              defaultMessage="Show {count, plural, one {# candidate} other {# candidates}}"
              values={{ count: filteredCount }}
            />
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default FiltersCandidatesModal;
